import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet";
import { withRouter } from "react-router-dom";
import { gql } from "apollo-boost";
import { useQuery, useMutation } from "react-apollo-hooks";
import { toast } from "react-toastify";
import { PageSettings } from "../../Config/PageSettings";

const FEE_TAX_FORM_INFO = gql`
  query FeeTaxFormInfo($token: String!) {
    FeeTaxFormInfo(token: $token) {
      valid
      feeTitle
      planner
    }
  }
`;

const FEE_TAX_INFO_SUBMIT = gql`
  mutation FeeTaxInfoSubmit(
    $token: String!
    $name: String!
    $residentNumber: String!
    $phone: String!
  ) {
    FeeTaxInfoSubmit(
      token: $token
      name: $name
      residentNumber: $residentNumber
      phone: $phone
    )
  }
`;

export default withRouter(({ match }) => {
  const token = match.params.token;
  const {
    handleSetPageSidebar,
    handleSetPageHeader,
    handleSetPageContentFullWidth,
  } = useContext(PageSettings);

  const { data, loading } = useQuery(FEE_TAX_FORM_INFO, {
    variables: { token },
    fetchPolicy: "network-only",
  });
  const [submitMutation] = useMutation(FEE_TAX_INFO_SUBMIT);

  const [name, setName] = useState("");
  const [residentNumber, setResidentNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    handleSetPageSidebar(false);
    handleSetPageHeader(false);
    handleSetPageContentFullWidth(true);
    return () => {
      handleSetPageSidebar(true);
      handleSetPageHeader(true);
      handleSetPageContentFullWidth(false);
    };
  }, [
    handleSetPageSidebar,
    handleSetPageHeader,
    handleSetPageContentFullWidth,
  ]);

  const info = data && data.FeeTaxFormInfo;

  const onSubmit = async (e) => {
    e.preventDefault();
    const digitsResident = residentNumber.replace(/\D/g, "");
    const digitsPhone = phone.replace(/\D/g, "");
    if (!name.trim()) return toast.error("이름을 입력해주세요.");
    if (digitsResident.length !== 13)
      return toast.error("주민등록번호 13자리를 정확히 입력해주세요.");
    if (digitsPhone.length < 10)
      return toast.error("연락처를 정확히 입력해주세요.");
    if (!agree) return toast.error("개인정보 수집·이용에 동의해주세요.");

    setSending(true);
    try {
      const {
        data: { FeeTaxInfoSubmit: ok },
      } = await submitMutation({
        variables: {
          token,
          name: name.trim(),
          residentNumber: digitsResident,
          phone: digitsPhone,
        },
      });
      if (ok) {
        setSubmitted(true);
      } else {
        toast.error("제출에 실패했습니다.");
      }
    } catch (err) {
      const msg = err.toString().replace("Error: GraphQL error:", "");
      toast.error(`${msg}`);
    } finally {
      setSending(false);
    }
  };

  const wrapStyle = {
    maxWidth: 460,
    margin: "0 auto",
    padding: "40px 20px",
  };

  return (
    <>
      <Helmet>
        <title>소득공제신고 내역 정보 제출 | 한밭페이</title>
      </Helmet>
      <div style={wrapStyle}>
        {!loading && info && info.valid ? (
          <div className="text-center m-b-20">
            <h2 className="m-b-5 f-w-700">{info.feeTitle}</h2>
            {info.planner ? (
              <div className="text-muted f-s-13">주관 : {info.planner}</div>
            ) : null}
            <div className="m-t-10 f-w-600">소득공제신고 내역 정보 제출</div>
          </div>
        ) : (
          <h3 className="text-center m-b-20">소득공제신고 내역 정보 제출</h3>
        )}

        {loading && <p className="text-center">불러오는 중...</p>}

        {!loading && (!info || !info.valid) && (
          <div className="alert alert-danger text-center">
            유효하지 않은 링크입니다. 담당자에게 문의해주세요.
          </div>
        )}

        {!loading && info && info.valid && submitted && (
          <div className="alert alert-success text-center">
            <i className="fa fa-check-circle m-r-5"></i>
            제출이 완료되었습니다. 감사합니다.
          </div>
        )}

        {!loading && info && info.valid && !submitted && (
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">정보 입력</h4>
            </div>
            <div className="panel-body">
              <div className="alert alert-info f-s-13">
                <i className="fa fa-info-circle m-r-5"></i>
                연말정산 소득공제신고 내역 발급을 위해 정보를 수집합니다. 가입하신
                <b> 연락처</b>로만 제출할 수 있습니다.
              </div>
              <form onSubmit={onSubmit}>
                <div className="form-group">
                  <label>이름</label>
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="이름"
                  />
                </div>
                <div className="form-group">
                  <label>주민등록번호</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    value={residentNumber}
                    onChange={(e) => setResidentNumber(e.target.value)}
                    placeholder="'-' 없이 13자리"
                    maxLength={14}
                  />
                </div>
                <div className="form-group">
                  <label>연락처</label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    className="form-control"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="가입하신 휴대폰 번호"
                  />
                </div>
                <div className="checkbox checkbox-css m-b-15">
                  <input
                    type="checkbox"
                    id="agree"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                  />
                  <label htmlFor="agree">
                    (필수) 소득공제신고 내역 발급 목적의 개인정보(주민등록번호) 수집·이용에
                    동의합니다.
                  </label>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary btn-block"
                  disabled={sending}
                >
                  {sending ? "제출 중..." : "제출"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
});
